import { useCallback, useEffect, useState } from "react";

import {
  getConnectedFirmEmails,
  getFaxSettings,
  getFaxTemplateVariables,
  patchFaxSettings,
} from "../api/faxSettingsApi";

const useFaxSettings = () => {
  const [settings, setSettings] = useState(null);
  const [variables, setVariables] = useState([]);
  const [firmEmails, setFirmEmails] = useState([]);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [loadingVariables, setLoadingVariables] = useState(true);
  const [loadingFirmEmails, setLoadingFirmEmails] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settingsError, setSettingsError] = useState(null);
  const [variablesError, setVariablesError] = useState(null);
  const [firmEmailsError, setFirmEmailsError] = useState(null);

  const loadSettings = useCallback(async () => {
    setLoadingSettings(true);
    setSettingsError(null);
    try {
      const response = await getFaxSettings();
      setSettings(response);
      return response;
    } catch (error) {
      setSettingsError(error);
      throw error;
    } finally {
      setLoadingSettings(false);
    }
  }, []);

  const loadVariables = useCallback(async () => {
    setLoadingVariables(true);
    setVariablesError(null);
    try {
      const response = await getFaxTemplateVariables();
      setVariables(response);
      return response;
    } catch (error) {
      setVariablesError(error);
      throw error;
    } finally {
      setLoadingVariables(false);
    }
  }, []);

  const loadFirmEmails = useCallback(async () => {
    setLoadingFirmEmails(true);
    setFirmEmailsError(null);
    try {
      const response = await getConnectedFirmEmails();
      setFirmEmails(response);
      return response;
    } catch (error) {
      setFirmEmailsError(error);
      throw error;
    } finally {
      setLoadingFirmEmails(false);
    }
  }, []);

  useEffect(() => {
    loadSettings().catch(() => {});
    loadVariables().catch(() => {});
    loadFirmEmails().catch(() => {});
  }, [loadFirmEmails, loadSettings, loadVariables]);

  const saveSettings = useCallback(
    async (payload) => {
      setSaving(true);
      try {
        const response = await patchFaxSettings(payload);
        const mergedSettings = { ...settings, ...response };
        if (
          !(payload instanceof FormData) &&
          Array.isArray(payload.selected_emails) &&
          !Array.isArray(response?.selected_emails)
        ) {
          mergedSettings.selected_emails = payload.selected_emails;
        }
        setSettings(mergedSettings);
        return mergedSettings;
      } finally {
        setSaving(false);
      }
    },
    [settings]
  );

  return {
    settings,
    variables,
    firmEmails,
    loadingSettings,
    loadingVariables,
    loadingFirmEmails,
    saving,
    settingsError,
    variablesError,
    firmEmailsError,
    loadSettings,
    loadVariables,
    loadFirmEmails,
    saveSettings,
  };
};

export default useFaxSettings;
